"use client";

import React, { useMemo } from "react";
import { Activity, Circle, Clock, Ear, Loader2, Mic, PhoneCall, X } from "lucide-react";

import { usePresenceSubscription } from "@/app/_module/hooks/usePresenceTracking";
import { useCoachSession } from "@/app/_module/contexts/CoachSessionContext";
import type { HierarchyCall } from "../../hooks/use-hierarchy-live-calls";
import { useGetAgencyUsers } from "../../hooks/use-hook";
import type { HierarchyTreeNode } from "../../services/agency-hierarchy";

/**
 * Live panel for one agency node in the hierarchy chart.
 *
 * Answers "what is this branch doing RIGHT NOW" — who's online, what page they're on,
 * how long they've been idle. Presence comes from the same `usePresenceSubscription`
 * channel the admin panel uses, so there is no second source of truth and no extra
 * polling: one realtime channel already carries `currentPage`, `lastSeen`, `device`
 * and `status` for every signed-in user.
 *
 *   hierarchy node ──► agency members (REST) ──┐
 *                                              ├──► joined by userId ──► live rows
 *   presence channel (realtime) ───────────────┘
 *
 * The join is done here rather than server-side because presence is inherently
 * client-state — the backend never sees "which page is this person looking at".
 */

/** "3m", "2h" — compact enough for a dense list, honest about precision. */
function since(iso?: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

const STATUS_TONE: Record<string, { dot: string; label: string }> = {
  online: { dot: "text-emerald-400 fill-emerald-400", label: "Online" },
  idle: { dot: "text-amber-400 fill-amber-400", label: "Idle" },
  offline: { dot: "text-slate-600 fill-slate-600", label: "Offline" },
};

export default function HierarchyLivePanel({
  node,
  onClose,
  accent,
  calls,
  uplineName,
}: {
  node: HierarchyTreeNode;
  onClose: () => void;
  accent: string;
  /** Owner of the agency above this one — this agency's direct upline. */
  uplineName?: string | null;
  /** Already scoped to this node by the parent, which owns the single live feed. */
  calls: HierarchyCall[];
}) {
  const { data: membersRaw, isLoading } = useGetAgencyUsers(node.id, true);
  const { onlineUsers } = usePresenceSubscription();
  const {
    isListening,
    callSid: activeCallSid,
    mode,
    duration: listenDuration,
    start: startSession,
    stop: stopSession,
    isStarting,
  } = useCoachSession();

  const members = useMemo<any[]>(() => {
    const d: any = membersRaw;
    // The agency-users endpoint has been wrapped differently over time; take the first
    // array we find rather than assuming a depth.
    return (
      (Array.isArray(d?.data?.items) && d.data.items) ||
      (Array.isArray(d?.items) && d.items) ||
      (Array.isArray(d?.data) && d.data) ||
      (Array.isArray(d) && d) ||
      []
    );
  }, [membersRaw]);

  /** Presence keyed by userId so the join below is O(1) per member. */
  const presenceById = useMemo(() => {
    const map = new Map<string, any>();
    for (const p of onlineUsers ?? []) if (p?.userId) map.set(p.userId, p);
    return map;
  }, [onlineUsers]);

  /**
   * The selected branch, flattened depth-first with its depth and owner — the order the
   * agent list is grouped by.
   */
  const branchAgencies = useMemo(() => {
    const out: { id: string; name: string; depth: number; ownerName: string | null }[] = [];
    const walk = (n: HierarchyTreeNode, depth: number) => {
      out.push({ id: n.id, name: n.name, depth, ownerName: n.ownerName });
      n.children.forEach((c) => walk(c, depth + 1));
    };
    walk(node, 0);
    return out;
  }, [node]);

  /**
   * Agents GROUPED BY AGENCY, in tree order — not one flat alphabetical list.
   *
   * ⚠️ The flat version was actively misleading: `includeSubAgencies=true` returns one row
   * per MEMBERSHIP, so anyone belonging to both a parent and a child agency appeared
   * twice with no way to tell why (Alfred Pennyworth, Barbara Gordon and Dick Grayson all
   * showed up doubled). Grouping by agency turns those duplicates into information —
   * you can see the person sits in both — and answers "who's their upline", since each
   * group is headed by its agency and that agency's owner.
   */
  const groups = useMemo(() => {
    const byAgency = new Map<string, any[]>();
    for (const m of members) {
      const agencyId = m.agencyId || m.agency?.id;
      if (!agencyId) continue;
      const userId = m.userId || m.user?.id || m.id;
      const presence = presenceById.get(userId);
      const row = {
        userId,
        name: m.user?.name || m.name || m.user?.email || "Unknown",
        role: m.role || m.user?.userType || "agent",
        status: presence?.status ?? "offline",
        currentPage: presence?.currentPage ?? null,
        lastSeen: presence?.lastSeen ?? null,
        // Who this person reports to — derived server-side from the hierarchy, the
        // same value the Agency Agents tab shows. Cascaded upline owners are already
        // excluded, so this is the DIRECT upline, not everyone above them.
        upline: (m.upline ?? []).map((u: any) => u?.name).filter(Boolean).join(", "),
      };
      const list = byAgency.get(agencyId);
      if (list) list.push(row);
      else byAgency.set(agencyId, [row]);
    }

    const rank = (s: string) => (s === "online" ? 0 : s === "idle" ? 1 : 2);
    return branchAgencies
      .map((a) => ({
        ...a,
        // Online first within each agency — an owner scanning this cares about who is
        // actually working right now.
        members: (byAgency.get(a.id) ?? []).sort(
          (x, y) => rank(x.status) - rank(y.status) || x.name.localeCompare(y.name),
        ),
      }))
      .filter((g) => g.members.length > 0);
  }, [members, presenceById, branchAgencies]);

  const rows = useMemo(() => groups.flatMap((g) => g.members), [groups]);
  const onlineCount = rows.filter((r) => r.status === "online").length;

  return (
    <aside
      className="flex h-full min-h-0 w-[320px] shrink-0 flex-col border-l border-slate-800 bg-[#0C1020]"
      aria-label={`Live activity for ${node.name}`}
    >
      <div className="flex items-start justify-between gap-2 border-b border-slate-800 px-4 py-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-100">{node.name}</div>
          {uplineName && (
            <div className="truncate text-[11px] text-slate-500" title={`${node.name} reports to ${uplineName}`}>
              Upline: <span className="text-slate-300">{uplineName}</span>
            </div>
          )}
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
            <span className="flex items-center gap-1">
              <Circle className="h-2 w-2 fill-emerald-400 text-emerald-400" />
              {onlineCount} online
            </span>
            <span>·</span>
            {/* People, not memberships. `rows` counts one entry per membership, so anyone
                sitting in both a parent and a child agency would be counted twice —
                which is exactly what made the flat list look like it had duplicates.
                "in branch" (not "members") because this spans the whole subtree while
                the card behind it shows the agency's OWN headcount. */}
            <span>
              {new Set(rows.map((r) => r.userId)).size} in branch
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close live panel"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-200"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* LIVE CALLS for this branch. `agencyId` already rides on every ActiveCall, and the
          eavesdrop endpoint returns sub-agency calls, so scoping is a client-side filter —
          no new endpoint. Listen/whisper reuse the app-wide CoachSessionContext, so a
          session survives tab switches exactly as it does in Agency Center. */}
      {calls.length > 0 && (
        <div className="max-h-[40%] shrink-0 overflow-y-auto border-b border-slate-800 px-2 py-2">
          <div className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Live calls · {calls.length}
          </div>
          {calls.map((c) => {
            const isThisCall = isListening && activeCallSid === c.callSid;
            return (
              <div
                key={c.callSid}
                className={`rounded-lg px-2 py-2 transition-colors ${
                  isThisCall ? "bg-white/10" : "hover:bg-white/5"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium text-slate-200">
                      {c.agentName || "Agent"}
                    </div>
                    <div className="truncate text-[10px] text-slate-500">
                      {c.leadName || c.contactName || c.contactNumber || "Unknown contact"}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      disabled={isListening || isStarting}
                      onClick={() =>
                        startSession(
                          {
                            callSid: c.callSid,
                            userId: c.userId,
                            agentName: c.agentName,
                            leadName: c.leadName,
                            contactName: c.contactName,
                          },
                          "listen",
                        )
                      }
                      title="Listen in"
                      aria-label={`Listen to ${c.agentName || "agent"}'s call`}
                      className="flex h-6 w-6 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-700 hover:text-slate-100 disabled:opacity-30"
                    >
                      {isStarting && !isListening ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Ear className="h-3 w-3" />
                      )}
                    </button>
                    <button
                      type="button"
                      disabled={isListening || isStarting}
                      onClick={() =>
                        startSession(
                          {
                            callSid: c.callSid,
                            userId: c.userId,
                            agentName: c.agentName,
                            leadName: c.leadName,
                            contactName: c.contactName,
                          },
                          "whisper",
                        )
                      }
                      title="Whisper (agent hears you, lead doesn't)"
                      aria-label={`Whisper to ${c.agentName || "agent"}`}
                      className="flex h-6 w-6 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-700 hover:text-slate-100 disabled:opacity-30"
                    >
                      <Mic className="h-3 w-3" />
                    </button>
                  </div>
                </div>

                {isThisCall && (
                  <div className="mt-1.5 flex items-center justify-between rounded bg-black/30 px-2 py-1">
                    <span className="text-[10px] font-medium" style={{ color: accent }}>
                      {mode === "whisper" ? "Whispering" : "Listening"} · {listenDuration}s
                    </span>
                    <button
                      type="button"
                      onClick={() => stopSession()}
                      className="text-[10px] font-semibold text-rose-300 transition-colors hover:text-rose-200"
                    >
                      Stop
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        {isLoading ? (
          <div className="space-y-2 p-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-800/50" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="px-3 py-8 text-center text-xs text-slate-500">
            No agents in this agency yet.
          </p>
        ) : (
          groups.map((g) => (
            <div key={g.id} className="mb-1">
              {/* Agency header = the group's upline. Indented by depth so the shape of
                  the org is visible in the list, not just in the chart. */}
              <div
                className="sticky top-0 z-10 flex items-baseline gap-2 bg-[#0C1020]/95 px-2 py-1.5 backdrop-blur-sm"
                style={{ paddingLeft: `${8 + g.depth * 12}px` }}
              >
                {g.depth > 0 && <span className="text-slate-700">└</span>}
                <span className="truncate text-[11px] font-semibold text-slate-300">
                  {g.name}
                </span>
                <span className="truncate text-[10px] text-slate-500">
                  {g.ownerName ? `· ${g.ownerName}` : "· no owner"}
                </span>
                <span className="ml-auto shrink-0 text-[10px] tabular-nums text-slate-600">
                  {g.members.length}
                </span>
              </div>

              {g.members.map((r) => {
                const tone = STATUS_TONE[r.status] ?? STATUS_TONE.offline;
                return (
              <div
                key={`${g.id}-${r.userId}`}
                className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-white/5"
                style={{ paddingLeft: `${8 + g.depth * 12}px` }}
              >
                <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-800 text-[11px] font-semibold text-slate-300">
                  {r.name.slice(0, 2).toUpperCase()}
                  <Circle
                    className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 ${tone.dot}`}
                    strokeWidth={0}
                  />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-medium text-slate-200">
                    {r.name}
                    {/* Only worth saying when it ISN'T the group's own owner. Every row
                        under "BATCAVE · Bruce Wayne" reporting to Bruce is already said
                        by the header — repeating it on each line just pushed the name
                        out of a 390px panel and truncated both. */}
                    {r.upline && r.upline !== g.ownerName && (
                      <span className="ml-1.5 font-normal text-slate-500" title={`Reports to ${r.upline}`}>
                        ↳ {r.upline}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 truncate text-[10px] text-slate-500">
                    {r.status === "offline" ? (
                      <span>{tone.label}</span>
                    ) : (
                      <>
                        <Activity className="h-2.5 w-2.5" style={{ color: accent }} />
                        <span className="truncate">{r.currentPage ?? tone.label}</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1 text-[10px] text-slate-500">
                  <Clock className="h-2.5 w-2.5" />
                  {since(r.lastSeen)}
                </div>
              </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      <div className="border-t border-slate-800 px-4 py-2.5">
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
          <PhoneCall className="h-3 w-3" />
          {calls.length > 0
            ? `${calls.length} call${calls.length === 1 ? "" : "s"} in progress · listen or whisper above`
            : "No calls in progress in this agency"}
        </div>
      </div>
    </aside>
  );
}
